const logicProductSeriesOrder = ['element','zinc','prism','rollo','delta-v','forza'];
function logicProductParseVariant(variant,tags = []) {
  const text = variant.options?.join(' / ') || variant.title || '';
  const seriesMatch = text.match(/\b(delta[ -]?v|element|prism|zinc|rollo|helix|forza)\b/i);
  const stampMatch = text.match(/\b(ACL\s*(?:PRO|COMP)|ACO)\b/i);
  const taggedSeries = tags.filter(tag => /^(element|zinc|prism|rollo|delta-v|forza|helix)$/i.test(tag));
  const taggedStamp = tags.filter(tag => /^(ACL PRO|ACL COMP|ACO)$/i.test(tag));
  const series = seriesMatch ? seriesMatch[1].toLowerCase().replace(/delta[ -]?v/,'delta-v') : taggedSeries.length === 1 ? taggedSeries[0].toLowerCase() : '';
  const stamp = /\b(?:no stamp|unstamped|none)\b/i.test(text) ? 'NONE' : stampMatch ? stampMatch[1].toUpperCase().replace(/ACL\s*/,'ACL ') : taggedStamp.length === 1 ? taggedStamp[0].toUpperCase() : '';
  const weightMatch = text.match(/(?:^|[\s/\-])([RLHS])(?=[\s(\-/]|$)/i);
  const wordWeight = text.match(/\b(light|regular|heavy)\b/i);
  const weight = stamp === 'ACL PRO' ? (weightMatch?.[1].toUpperCase() || (wordWeight ? {light:'L',regular:'R',heavy:'H'}[wordWeight[1].toLowerCase()] : '')) : '';
  const supported = logicProductSeriesOrder.includes(series) && ['ACL PRO','ACL COMP'].includes(stamp) && weight !== 'S' && (series !== 'forza' || stamp === 'ACL COMP');
  return {...variant,series,stamp,weight,supported,available:variant.available ?? variant.availableForSale ?? false};
}
function logicProductSelectionDimensions(options,variants) {
  return new Set(options.map((name,index) => {
    if (/series|weight|stamp|bag type/i.test(name)) return index;
    if (variants.some(variant => /\b(delta[ -]?v|element|prism|zinc|rollo|helix|forza|ACL\s*(?:PRO|COMP)|ACO)\b/i.test(variant.options?.[index] || ''))) return index;
    return null;
  }).filter(index => index !== null));
}
function logicProductSeriesTarget(variants,current,series,dimensions,preferredWeight = '') {
  const candidates = variants.filter(variant => variant.supported && variant.available && variant.series === series && (variant.options || []).every((value,index) => dimensions.has(index) || value === current.options?.[index]));
  const score = variant => {
    let value = variant.stamp === current.stamp ? 4 : 0;
    if (variant.stamp === 'ACL PRO') {
      if (preferredWeight && variant.weight === preferredWeight) value += 100;
      else if (variant.weight === 'R') value += 20;
    }
    return value;
  };
  return candidates.sort((a,b) => score(b)-score(a))[0] || null;
}
function logicProductSwipeDirection(start,end) {
  const dx = end.x-start.x, dy = end.y-start.y;
  if (end.time-start.time > 900 || Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)*1.6) return 0;
  return dx < 0 ? 1 : -1;
}
class LogicProductSeries extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if (this.dataset.enabled !== 'true' || this.abort) return;
    this.productInfo = this.closest('product-info');
    this.picker = this.productInfo?.querySelector('variant-selects');
    const script = this.querySelector('[data-series-data]');
    if (!this.picker || !script) return;
    try { this.data = JSON.parse(script.textContent); } catch { return; }
    this.variants = this.data.variants.map(variant => logicProductParseVariant(variant,this.data.tags));
    this.current = this.variants.find(variant => String(variant.id) === this.dataset.selectedVariant);
    if (!this.current?.supported) return;
    if (this.current.stamp === 'ACL PRO' && ['R','L','H'].includes(this.current.weight)) this.productInfo.dataset.logicProWeight = this.current.weight;
    this.preferredWeight = this.productInfo.dataset.logicProWeight || this.current.weight;
    this.dimensions = logicProductSelectionDimensions(this.data.options,this.variants);
    this.entries = logicProductSeriesOrder.map(series => ({series,variant:logicProductSeriesTarget(this.variants,this.current,series,this.dimensions,this.preferredWeight)})).filter(entry => entry.variant || entry.series === this.current.series);
    this.index = this.entries.findIndex(entry => entry.series === this.current.series);
    if (this.entries.length < 2 || this.index < 0) return;
    this.abort = new AbortController();
    this.querySelector('[data-series-controls]').hidden = false;
    this.dataset.ready = 'true';
    this.tabIndex = 0;
    this.setAttribute('role','group');
    this.busy = false;
    this.sync();
    const options = {signal:this.abort.signal};
    this.addEventListener('click',event => {
      const button = event.target.closest('[data-series-direction]');
      if (button) this.select(Number(button.dataset.seriesDirection),true);
    },options);
    this.addEventListener('keydown',event => {
      if (event.target !== this && !event.target.closest('[data-series-direction]')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.select(event.key === 'ArrowRight' ? 1 : -1,true);
      }
    },options);
    this.addEventListener('pointerdown',event => {
      if (!event.isPrimary || !['touch','pen'].includes(event.pointerType) || event.target.closest('button,a,input,select,textarea')) return;
      this.gesture = {x:event.clientX,y:event.clientY,time:event.timeStamp,id:event.pointerId};
      this.setPointerCapture(event.pointerId);
    },options);
    this.addEventListener('pointermove',event => {
      if (!this.gesture || event.pointerId !== this.gesture.id) return;
      if (Math.abs(event.clientY-this.gesture.y) > 12 && Math.abs(event.clientY-this.gesture.y) > Math.abs(event.clientX-this.gesture.x)) this.gesture = null;
    },options);
    this.addEventListener('pointerup',event => {
      const start = this.gesture;
      this.gesture = null;
      if (!start || start.id !== event.pointerId) return;
      const direction = logicProductSwipeDirection(start,{x:event.clientX,y:event.clientY,time:event.timeStamp});
      if (direction) this.select(direction,false);
    },options);
    this.addEventListener('pointercancel',() => {this.gesture = null;},options);
    this.productInfo.addEventListener('product-info:error',() => {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.querySelector('[data-series-error]').hidden = false;
      delete this.productInfo.dataset.logicSeriesFocus;
      this.sync();
    },options);
    const focusDirection = this.productInfo.dataset.logicSeriesFocus;
    if (focusDirection) requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const button = this.querySelector('[data-series-direction="' + focusDirection + '"]');
      (button && !button.disabled ? button : this).focus({preventScroll:true});
      delete this.productInfo.dataset.logicSeriesFocus;
    });
  }
  disconnectedCallback() { this.abort?.abort(); this.abort = null; }
  sync() {
    this.querySelectorAll('[data-series-direction]').forEach(button => {
      const target = this.entries[this.index + Number(button.dataset.seriesDirection)];
      button.disabled = this.busy || !target?.variant;
    });
    this.querySelector('[data-series-position]').textContent = this.data.position.replace('__CURRENT__',this.index+1).replace('__TOTAL__',this.entries.length);
  }
  select(direction,restoreFocus) {
    if (this.busy) return;
    const entry = this.entries[this.index+direction];
    if (!entry?.variant) return;
    const picker = this.productInfo.querySelector('variant-selects');
    const groups = [...picker.querySelectorAll('.product-form__input')];
    const selections = entry.variant.options.map((value,index) => {
      const group = groups[index];
      if (!group) return null;
      const select = group.querySelector('select');
      const control = select ? [...select.options].find(option => option.value === value) : [...group.querySelectorAll('input[type="radio"]')].find(input => input.value === value);
      return control ? {group,select,control,value,index} : null;
    });
    if (selections.some(selection => !selection)) {
      this.querySelector('[data-series-error]').hidden = false;
      return;
    }
    for (const {group,select,control,value} of selections) {
      if (select) {
        select.value = value;
        [...select.options].forEach(option => option.toggleAttribute('selected',option === control));
      } else group.querySelectorAll('input[type="radio"]').forEach(input => {input.checked = input === control;});
    }
    const changed = selections.find(selection => this.dimensions.has(selection.index) && this.current.options[selection.index] !== selection.value) || selections[0];
    this.busy = true;
    this.setAttribute('aria-busy','true');
    this.querySelector('[data-series-error]').hidden = true;
    if (restoreFocus) this.productInfo.dataset.logicSeriesFocus = String(direction);
    this.sync();
    (changed.select || changed.control).dispatchEvent(new Event('change',{bubbles:true}));
  }
}
if (typeof customElements !== 'undefined' && !customElements.get('logic-product-series')) customElements.define('logic-product-series',LogicProductSeries);
if (typeof module !== 'undefined' && module.exports) module.exports = {logicProductParseVariant,logicProductSelectionDimensions,logicProductSeriesTarget,logicProductSwipeDirection,LogicProductSeries};
